/**
 * Module dependencies.
 */

import { ModalPortal } from 'src/components/core/modal/portal';
import { ReactNode } from 'react';
import { Svg } from 'src/components/core/svg';
import { media } from '@untile/react-core/styles/media';
import { motion } from 'framer-motion';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import closeSvg from 'src/assets/svgs/close.svg';
import styled from 'styled-components';

/**
 * `BaseModalProps` type.
 */

export type BaseModalProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
};

/**
 * `Backdrop` styled component.
 */

const Backdrop = styled(motion.div)`
  background-color: rgba(2, 2, 6, 0.7);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--z-index-modal-backdrop);
`;

/**
 * `WrapperModal` styled component.
 */

const WrapperModal = styled(motion.div)`
  --modal-call-me-box-shadow-desktop: 0 2.767px 2.214px 0 ${transparentize('black', 0.3)},
    0 6.65px 5.32px 0 ${transparentize('black', 0.04)}, 0 12.522px 10.017px 0 ${transparentize('black', 0.05)},
    0 22.336px 17.869px 0 ${transparentize('black', 0.07)}, 0 41.778px 33.422px 0 ${transparentize('black', 0.08)},
    0 100px 80px 0 ${transparentize('black', 0.11)};

  border-top-left-radius: var(--space-sm);
  border-top-right-radius: var(--space-sm);
  bottom: 0;
  box-shadow:
    0 -16px 40px 0 ${transparentize('black', 0.9)},
    0 4px 4px 0 ${transparentize('black', 0.25)};
  left: 50%;
  max-height: 100vh;
  max-width: 375px;
  overflow-y: auto;
  position: absolute;
  transform: translate3d(-50%, 0, 0);
  width: 100%;

  ::-webkit-scrollbar {
    display: none;
  }

  ${media.min.sm`
    border-radius: var(--space-sm);
    bottom: 50%;
    box-shadow: var(--modal-call-me-box-shadow-desktop);
  `}
`;

/**
 * `Modal` styled component.
 */

const Modal = styled.div`
  align-items: center;
  background: var(--gradient-secondary--45-deg);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px var(--space-sm) 40px;
  position: relative;
  z-index: var(--z-index-modal);
`;

/**
 * `CloseButton` styled component.
 */

const CloseButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  padding: 10px;
  position: absolute;
  right: var(--space-xxs);
  top: var(--space-xxs);
`;

/**
 * Export `BaseModal` component.
 */

export const BaseModal = ({ children, isOpen, onClose, showCloseButton = false }: BaseModalProps) => {
  const isMobile = useBreakpoint(0, 'sm');

  return (
    <ModalPortal isOpen={isOpen} onRequesClose={onClose} preventBodyScroll={isOpen}>
      {() => (
        <Backdrop animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
          <WrapperModal
            animate={{ opacity: 1, y: isMobile ? 0 : '50%' }}
            exit={{ opacity: 0, y: isMobile ? 100 : -100 }}
            initial={{ opacity: 0, x: '-50%', y: isMobile ? 100 : -100 }}
            onClick={event => event.stopPropagation()}
          >
            <Modal>
              {showCloseButton && (
                <CloseButton aria-label={'Close'} onClick={onClose}>
                  <Svg icon={closeSvg} size={'20px'} />
                </CloseButton>
              )}

              {children}
            </Modal>
          </WrapperModal>
        </Backdrop>
      )}
    </ModalPortal>
  );
};
